import { useState, useRef, useEffect } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Card from '../../../components/base/Card';

interface Template {
  id: string;
  name: string;
  description: string;
  backgroundImage: string;
  fields: TemplateField[];
  createdAt: string;
  status: 'active' | 'draft';
  orientation?: 'portrait' | 'landscape';
  canvasWidth?: number;
  canvasHeight?: number;
}

interface TemplateField {
  id: string;
  name: string;
  excelColumn: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  fontWeight: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
  color: string;
  align: 'left' | 'center' | 'right';
  textDecoration: 'none' | 'underline' | 'line-through';
  lineHeight: number;
  letterSpacing: number;
  backgroundColor?: string;
  borderWidth?: number;
  borderColor?: string;
  borderRadius?: number;
  padding?: number;
  type?: 'text' | 'image';
  imageUrl?: string;
}

interface ExcelMapping {
  column: string;
  description: string;
}

// 전역 엑셀 컬럼 매핑 (로컬 스토리지에서 관리)
const getGlobalExcelMappings = (): ExcelMapping[] => {
  const saved = localStorage.getItem('excelMappings');
  if (saved) {
    return JSON.parse(saved);
  }
  return [
    { column: 'A', description: '이름' },
    { column: 'B', description: '과정명' },
    { column: 'C', description: '발급일' },
    { column: 'D', description: '기관명' },
    { column: 'E', description: '수료번호' },
    { column: 'F', description: '교육기간' },
    { column: 'G', description: '교육시간' },
    { column: 'H', description: '성적' },
    { column: 'I', description: '담당강사' },
    { column: 'J', description: '발급기관' }
  ];
};

const saveGlobalExcelMappings = (mappings: ExcelMapping[]) => {
  localStorage.setItem('excelMappings', JSON.stringify(mappings));
};

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([
    {
      id: '1',
      name: '수료증 템플릿',
      description: '기본 수료증 템플릿',
      backgroundImage: 'https://public.readdy.ai/ai/img_res/b5166db431184b5ab5d924178d493f55.jpg',
      fields: [
        { id: '1', name: '이름', excelColumn: 'A', x: 400, y: 250, width: 200, height: 40, fontSize: 24, fontFamily: 'Arial', fontWeight: 'normal', color: '#000000', align: 'center', textDecoration: 'none', lineHeight: 1.2, letterSpacing: 0, type: 'text' },
        { id: '2', name: '과정명', excelColumn: 'B', x: 400, y: 320, width: 300, height: 30, fontSize: 18, fontFamily: 'Arial', fontWeight: 'normal', color: '#000000', align: 'center', textDecoration: 'none', lineHeight: 1.2, letterSpacing: 0, type: 'text' },
        { id: '3', name: '발급일', excelColumn: 'C', x: 600, y: 450, width: 150, height: 25, fontSize: 14, fontFamily: 'Arial', fontWeight: 'normal', color: '#000000', align: 'center', textDecoration: 'none', lineHeight: 1.2, letterSpacing: 0, type: 'text' }
      ],
      createdAt: '2024-01-15',
      status: 'active',
      orientation: 'landscape',
      canvasWidth: 800,
      canvasHeight: 600
    }
  ]);

  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '' });
  const [canvas, setCanvas] = useState<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selectedField, setSelectedField] = useState<TemplateField | null>(null);
  const [excelColumns] = useState([
    'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'
  ]);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [backgroundSettings, setBackgroundSettings] = useState({
    type: 'image',
    color: '#ffffff',
    imageUrl: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageUploadRef = useRef<HTMLInputElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'mappings'>('list');
  const [canvasOrientation, setCanvasOrientation] = useState<'portrait' | 'landscape'>('landscape');
  const [draggedColumn, setDraggedColumn] = useState<ExcelMapping | null>(null);
  const [uploadedImages, setUploadedImages] = useState<Array<{ id: string; url: string; name: string }>>([]);
  const [draggedImage, setDraggedImage] = useState<{ id: string; url: string; name: string } | null>(null);
  const [leftPanelTab, setLeftPanelTab] = useState<'columns' | 'canvas-info' | 'properties'>('columns');
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);

  // 전역 엑셀 컬럼 매핑 상태
  const [globalExcelMappings, setGlobalExcelMappings] = useState<ExcelMapping[]>(getGlobalExcelMappings());

  // 전역 매핑 업데이트 함수
  const updateGlobalExcelMappings = (mappings: ExcelMapping[]) => {
    setGlobalExcelMappings(mappings);
    saveGlobalExcelMappings(mappings);
  };

  // Fabric.js 동적 로딩
  const loadFabric = async () => {
    try {
      const fabricModule = await import('fabric');
      return fabricModule;
    } catch (error) {
      console.error('Fabric.js 로딩 실패:', error);
      return null;
    }
  };

  // A4 크기 계산 (픽셀 단위, 72 DPI 기준)
  const getA4Dimensions = (orientation: 'portrait' | 'landscape') => {
    const A4_WIDTH_MM = 210;
    const A4_HEIGHT_MM = 297;
    const MM_TO_PX = 3.7795275591;
    
    const width = Math.round(A4_WIDTH_MM * MM_TO_PX);
    const height = Math.round(A4_HEIGHT_MM * MM_TO_PX);
    
    return orientation === 'portrait' 
      ? { width, height }
      : { width: height, height: width };
  };

  // 캔버스 방향 변경 함수
  const changeCanvasOrientation = async (orientation: 'portrait' | 'landscape') => {
    if (!canvas || !selectedTemplate) return;

    try {
      const dimensions = getA4Dimensions(orientation);
      
      // 캔버스 크기 변경
      canvas.setDimensions({
        width: dimensions.width,
        height: dimensions.height
      });

      // 배경 이미지가 있으면 다시 로드
      if (selectedTemplate.backgroundImage) {
        const fabricModule = await loadFabric();
        if (fabricModule) {
          const { FabricImage } = fabricModule;
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const fabricImg = new FabricImage(img, {
                left: 0,
                top: 0,
                selectable: false,
                evented: false
              });
              
              fabricImg.scaleToWidth(dimensions.width);
              fabricImg.scaleToHeight(dimensions.height);
              
              canvas.backgroundImage = fabricImg;
              canvas.renderAll();
            } catch (error) {
              console.warn('배경 이미지 조정 실패:', error);
            }
          };
          img.src = selectedTemplate.backgroundImage;
        }
      } else if (backgroundSettings.type === 'color') {
        canvas.backgroundColor = backgroundSettings.color;
      }

      setCanvasOrientation(orientation);
      
      // 템플릿 정보 업데이트
      setSelectedTemplate({
        ...selectedTemplate,
        orientation,
        canvasWidth: dimensions.width,
        canvasHeight: dimensions.height
      });

      canvas.renderAll();
      
      alert(`캔버스 방향이 ${orientation === 'landscape' ? 'A4 가로' : 'A4 세로'}로 변경되었습니다.`);
    } catch (error) {
      console.error('캔버스 방향 변경 오류:', error);
      alert('캔버스 방향 변경에 실패했습니다.');
    }
  };

  useEffect(() => {
    let fabricCanvas: any = null;

    const initializeCanvas = async () => {
      if (isEditorOpen && canvasRef.current && !canvas) {
        try {
          const fabricModule = await loadFabric();
          if (!fabricModule) {
            alert('템플릿 편집기 로딩에 실패했습니다. 페이지를 새로고침해주세요.');
            return;
          }

          const { Canvas } = fabricModule;

          const initialOrientation = selectedTemplate?.orientation || 'landscape';
          const dimensions = getA4Dimensions(initialOrientation);
          setCanvasOrientation(initialOrientation);

          fabricCanvas = new Canvas(canvasRef.current, {
            width: dimensions.width,
            height: dimensions.height,
            backgroundColor: '#ffffff',
            selection: true,
            preserveObjectStacking: true,
            renderOnAddRemove: true,
            enableRetinaScaling: true
          });

          fabricCanvas.on('object:modified', (e: any) => {
            const activeObject = e.target;
            if (activeObject && activeObject.data) {
              const field = activeObject.data as TemplateField;
              field.x = activeObject.left || 0;
              field.y = activeObject.top || 0;
              field.width = (activeObject.width || 200) * (activeObject.scaleX || 1);
              field.height = (activeObject.height || 40) * (activeObject.scaleY || 1);
              
              if (selectedTemplate) {
                const updatedFields = selectedTemplate.fields.map(f =>
                  f.id === field.id ? field : f
                );
                setSelectedTemplate({
                  ...selectedTemplate,
                  fields: updatedFields
                });
              }
            }
          });

          fabricCanvas.on('selection:created', (e: any) => {
            const activeObject = e.selected?.[0];
            if (activeObject && activeObject.data) {
              setSelectedField(activeObject.data);
              setLeftPanelTab('properties');
            }
          });

          fabricCanvas.on('selection:updated', (e: any) => {
            const activeObject = e.selected?.[0];
            if (activeObject && activeObject.data) {
              setSelectedField(activeObject.data);
              setLeftPanelTab('properties');
            }
          });

          fabricCanvas.on('selection:cleared', () => {
            setSelectedField(null);
          });

          setCanvas(fabricCanvas);
          setIsCanvasReady(true);

        } catch (error) {
          console.error('캔버스 초기화 오류:', error);
          alert('템플릿 편집기 초기화에 실패했습니다. 페이지를 새로고침해주세요.');
        }
      }
    };

    initializeCanvas();

    return () => {
      if (fabricCanvas) {
        try {
          fabricCanvas.dispose();
        } catch (error) {
          console.warn('캔버스 정리 오류:', error);
        }
      }
      setCanvas(null);
      setIsCanvasReady(false);
    };
  }, [isEditorOpen]);

  const openEditor = (template: Template) => {
    setSelectedTemplate(template);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setSelectedTemplate(null);
    setSelectedField(null);
    if (canvas) {
      try {
        canvas.dispose();
      } catch (error) {
        console.warn('캔버스 정리 오류:', error);
      }
      setCanvas(null);
    }
    setIsCanvasReady(false);
    setUploadedImages([]);
  };

  const loadTemplateToCanvas = async () => {
    if (!canvas || !selectedTemplate || !isCanvasReady) return;

    try {
      const fabricModule = await loadFabric();
      if (!fabricModule) return;

      const { Textbox, Rect, FabricImage } = fabricModule;

      canvas.clear();

      const templateOrientation = selectedTemplate.orientation || 'landscape';
      const dimensions = getA4Dimensions(templateOrientation);
      
      canvas.setDimensions({
        width: dimensions.width,
        height: dimensions.height
      });
      
      setCanvasOrientation(templateOrientation);

      // 배경 사각형 추가
      const backgroundRect = new Rect({
        left: 0,
        top: 0,
        width: dimensions.width,
        height: dimensions.height,
        fill: '#f8f9fa',
        selectable: false,
        evented: false,
        hoverCursor: 'default'
      });

      canvas.add(backgroundRect);
      canvas.sendObjectToBack(backgroundRect);

      // 배경 이미지 로드
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const fabricImg = new FabricImage(img, {
            left: 0,
            top: 0,
            selectable: false,
            evented: false,
            hoverCursor: 'default'
          });
          
          fabricImg.scaleToWidth(dimensions.width);
          fabricImg.scaleToHeight(dimensions.height);
          
          canvas.add(fabricImg);
          canvas.sendObjectToBack(fabricImg);
          canvas.renderAll();
        } catch (error) {
          console.warn('배경 이미지 로딩 실패:', error);
        }
      };
      
      img.onerror = () => {
        console.warn('배경 이미지 로딩 실패, 기본 배경 사용');
      };
      
      img.src = selectedTemplate.backgroundImage;

      // 필드 추가
      selectedTemplate.fields.forEach((field) => {
        try {
          if (field.type === 'image' && field.imageUrl) {
            const imgElement = new Image();
            imgElement.crossOrigin = 'anonymous';
            imgElement.onload = () => {
              const fabricImg = new FabricImage(imgElement, {
                left: field.x,
                top: field.y,
                scaleX: field.width / imgElement.width,
                scaleY: field.height / imgElement.height,
                borderColor: '#3b82f6',
                cornerColor: '#3b82f6',
                cornerSize: 12,
                transparentCorners: false,
                cornerStrokeColor: '#ffffff',
                hasControls: true,
                hasBorders: true,
                lockRotation: true,
                selectable: true,
                evented: true,
                hoverCursor: 'move',
                moveCursor: 'move'
              });
              fabricImg.data = field;
              fabricImg.setControlsVisibility({
                mt: true,
                mb: true,
                ml: true,
                mr: true,
                bl: true,
                br: true,
                tl: true,
                tr: true,
                mtr: false
              });
              canvas.add(fabricImg);
              canvas.bringObjectToFront(fabricImg);
              canvas.renderAll();
            };
            imgElement.src = field.imageUrl;
          } else {
            const textObj = new Textbox(`[${field.name}]`, {
              left: field.x,
              top: field.y,
              width: field.width,
              fontSize: field.fontSize,
              fontFamily: field.fontFamily,
              fontWeight: field.fontWeight || 'normal',
              fill: field.color,
              textAlign: field.align,
              underline: field.textDecoration === 'underline',
              linethrough: field.textDecoration === 'line-through',
              lineHeight: field.lineHeight || 1.2,
              charSpacing: (field.letterSpacing || 0) * 1000,
              backgroundColor: field.backgroundColor === 'transparent' ? '' : (field.backgroundColor || ''),
              stroke: field.borderWidth && field.borderWidth > 0 ? field.borderColor : '',
              strokeWidth: field.borderWidth || 0,
              rx: field.borderRadius || 0,
              ry: field.borderRadius || 0,
              borderColor: '#3b82f6',
              cornerColor: '#3b82f6',
              cornerSize: 12,
              transparentCorners: false,
              cornerStrokeColor: '#ffffff',
              hasControls: true,
              hasBorders: true,
              lockRotation: true,
              selectable: true,
              evented: true,
              hoverCursor: 'move',
              moveCursor: 'move',
              editable: true
            });

            textObj.data = field;
            textObj.setControlsVisibility({
              mt: true,
              mb: true,
              ml: true,
              mr: true,
              bl: true,
              br: true,
              tl: true,
              tr: true,
              mtr: false
            });
            canvas.add(textObj);
            canvas.bringObjectToFront(textObj);
          }
        } catch (error) {
          console.error('필드 생성 오류:', error);
        }
      });

      canvas.renderAll();
      canvas.requestRenderAll();
    } catch (error) {
      console.error('템플릿 로딩 오류:', error);
      alert('템플릿 로딩에 실패했습니다.');
    }
  };

  useEffect(() => {
    if (canvas && selectedTemplate && isCanvasReady) {
      loadTemplateToCanvas();
    }
  }, [canvas, selectedTemplate, isCanvasReady]);

  // 엑셀 컬럼 드래그 시작
  const handleColumnDragStart = (e: React.DragEvent, mapping: ExcelMapping) => {
    setDraggedColumn(mapping);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // 이미지 드래그 시작
  const handleImageDragStart = (e: React.DragEvent, image: { id: string; url: string; name: string }) => {
    setDraggedImage(image);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // 캔버스 드롭 처리
  const handleCanvasDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!canvas || !isCanvasReady) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    try {
      const fabricModule = await loadFabric();
      if (!fabricModule) return;

      if (draggedColumn) {
        const { Textbox } = fabricModule;

        const newField: TemplateField = {
          id: Date.now().toString(),
          name: draggedColumn.description,
          excelColumn: draggedColumn.column,
          x,
          y,
          width: 200,
          height: 40,
          fontSize: 16,
          fontFamily: 'Arial',
          fontWeight: 'normal',
          color: '#000000',
          align: 'left',
          textDecoration: 'none',
          lineHeight: 1.2,
          letterSpacing: 0,
          backgroundColor: 'transparent',
          borderWidth: 0,
          borderColor: '#000000',
          borderRadius: 0,
          padding: 0,
          type: 'text'
        };

        const textObj = new Textbox(`[${newField.name}]`, {
          left: newField.x,
          top: newField.y,
          width: newField.width,
          fontSize: newField.fontSize,
          fontFamily: newField.fontFamily,
          fontWeight: newField.fontWeight,
          fill: newField.color,
          textAlign: newField.align,
          borderColor: '#3b82f6',
          cornerColor: '#3b82f6',
          cornerSize: 12,
          transparentCorners: false,
          cornerStrokeColor: '#ffffff',
          hasControls: true,
          hasBorders: true,
          lockRotation: true,
          selectable: true,
          evented: true,
          hoverCursor: 'move',
          moveCursor: 'move',
          editable: true
        });

        textObj.data = newField;
        textObj.setControlsVisibility({
          mt: true,
          mb: true,
          ml: true,
          mr: true,
          bl: true,
          br: true,
          tl: true,
          tr: true,
          mtr: false
        });
        canvas.add(textObj);
        canvas.bringObjectToFront(textObj);
        canvas.setActiveObject(textObj);
        canvas.renderAll();

        if (selectedTemplate) {
          setSelectedTemplate({
            ...selectedTemplate,
            fields: [...selectedTemplate.fields, newField]
          });
        }

        setSelectedField(newField);
        setLeftPanelTab('properties');
        setDraggedColumn(null);
      } else if (draggedImage) {
        const { FabricImage } = fabricModule;

        const imgElement = new Image();
        imgElement.crossOrigin = 'anonymous';
        imgElement.onload = () => {
          const newField: TemplateField = {
            id: Date.now().toString(),
            name: draggedImage.name,
            excelColumn: '',
            x,
            y,
            width: imgElement.width,
            height: imgElement.height,
            fontSize: 16,
            fontFamily: 'Arial',
            fontWeight: 'normal',
            color: '#000000',
            align: 'left',
            textDecoration: 'none',
            lineHeight: 1.2,
            letterSpacing: 0,
            type: 'image',
            imageUrl: draggedImage.url
          };

          const fabricImg = new FabricImage(imgElement, {
            left: x,
            top: y,
            borderColor: '#3b82f6',
            cornerColor: '#3b82f6',
            cornerSize: 12,
            transparentCorners: false,
            cornerStrokeColor: '#ffffff',
            hasControls: true,
            hasBorders: true,
            lockRotation: true,
            selectable: true,
            evented: true,
            hoverCursor: 'move',
            moveCursor: 'move'
          });

          fabricImg.data = newField;
          fabricImg.setControlsVisibility({
            mt: true,
            mb: true,
            ml: true,
            mr: true,
            bl: true,
            br: true,
            tl: true,
            tr: true,
            mtr: false
          });
          canvas.add(fabricImg);
          canvas.bringObjectToFront(fabricImg);
          canvas.setActiveObject(fabricImg);
          canvas.renderAll();

          if (selectedTemplate) {
            setSelectedTemplate({
              ...selectedTemplate,
              fields: [...selectedTemplate.fields, newField]
            });
          }

          setSelectedField(newField);
          setLeftPanelTab('properties');
          setDraggedImage(null);
        };
        imgElement.src = draggedImage.url;
      }
    } catch (error) {
      console.error('드롭 처리 오류:', error);
      alert('요소 추가에 실패했습니다.');
    }
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/png')) {
        alert('PNG 이미지만 업로드 가능합니다.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now().toString() + Math.random(),
          url: e.target?.result as string,
          name: file.name
        };
        setUploadedImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
  };

  const updateField = (updates: Partial<TemplateField>) => {
    if (!selectedField || !canvas || !selectedTemplate) return;

    try {
      const updatedField = { ...selectedField, ...updates };
      const activeObject = canvas.getActiveObject();

      if (activeObject && selectedField.type !== 'image') {
        if (updates.fontSize) activeObject.set('fontSize', updates.fontSize);
        if (updates.color) activeObject.set('fill', updates.color);
        if (updates.fontFamily) activeObject.set('fontFamily', updates.fontFamily);
        if (updates.fontWeight) activeObject.set('fontWeight', updates.fontWeight);
        if (updates.align) activeObject.set('textAlign', updates.align);
        if (updates.name) activeObject.set('text', `[${updates.name}]`);
        if (updates.textDecoration !== undefined) {
          activeObject.set('underline', updates.textDecoration === 'underline');
          activeObject.set('linethrough', updates.textDecoration === 'line-through');
        }
        if (updates.lineHeight) activeObject.set('lineHeight', updates.lineHeight);
        if (updates.letterSpacing !== undefined) activeObject.set('charSpacing', updates.letterSpacing * 1000);
        if (updates.backgroundColor !== undefined) {
          activeObject.set('backgroundColor', updates.backgroundColor === 'transparent' ? '' : updates.backgroundColor);
        }
        if (updates.borderWidth !== undefined || updates.borderColor) {
          activeObject.set('stroke', updates.borderWidth && updates.borderWidth > 0 ? (updates.borderColor || selectedField.borderColor) : '');
          activeObject.set('strokeWidth', updates.borderWidth || 0);
        }
        if (updates.borderRadius !== undefined) {
          activeObject.set('rx', updates.borderRadius);
          activeObject.set('ry', updates.borderRadius);
        }

        activeObject.data = updatedField;
        canvas.renderAll();
      }

      setSelectedField(updatedField);

      const updatedFields = selectedTemplate.fields.map(field =>
        field.id === selectedField.id ? updatedField : field
      );

      setSelectedTemplate({
        ...selectedTemplate,
        fields: updatedFields
      });
    } catch (error) {
      console.error('필드 업데이트 오류:', error);
      alert('필드 업데이트에 실패했습니다.');
    }
  };

  const updateBackgroundColor = (color: string) => {
    if (!canvas || !isCanvasReady) return;

    try {
      canvas.backgroundColor = color;
      canvas.renderAll();
      setBackgroundSettings(prev => ({ ...prev, type: 'color', color }));
    } catch (error) {
      console.error('배경색 변경 오류:', error);
    }
  };

  const uploadBackgroundImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !canvas || !isCanvasReady) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const fabricModule = await loadFabric();
        if (!fabricModule) return;

        const { FabricImage } = fabricModule;
        const img = new Image();
        img.onload = () => {
          try {
            const fabricImg = new FabricImage(img, {
              left: 0,
              top: 0,
              selectable: false,
              evented: false
            });
            
            fabricImg.scaleToWidth(canvas.width);
            fabricImg.scaleToHeight(canvas.height);
            
            canvas.backgroundImage = fabricImg;
            canvas.renderAll();
            
            setBackgroundSettings(prev => ({ 
              ...prev, 
              type: 'image', 
              imageUrl: e.target?.result as string 
            }));
          } catch (error) {
            console.error('배경 이미지 설정 오류:', error);
            alert('배경 이미지 설정에 실패했습니다.');
          }
        };
        img.src = e.target?.result as string;
      } catch (error) {
        console.error('이미지 로딩 오류:', error);
        alert('이미지 로딩에 실패했습니다.');
      }
    };
    reader.readAsDataURL(file);
  };

  const removeBackground = () => {
    if (!canvas || !isCanvasReady) return;

    try {
      canvas.backgroundColor = '#ffffff';
      canvas.backgroundImage = null;
      canvas.renderAll();
      setBackgroundSettings({ type: 'none', color: '#ffffff', imageUrl: '' });
    } catch (error) {
      console.error('배경 제거 오류:', error);
    }
  };

  const deleteSelectedField = () => {
    if (!canvas || !selectedField) return;

    try {
      const activeObject = canvas.getActiveObject();
      if (activeObject) {
        canvas.remove(activeObject);
        canvas.renderAll();
        
        if (selectedTemplate) {
          const updatedFields = selectedTemplate.fields.filter(field => field.id !== selectedField.id);
          setSelectedTemplate({
            ...selectedTemplate,
            fields: updatedFields
          });
        }
        
        setSelectedField(null);
      }
    } catch (error) {
      console.error('필드 삭제 오류:', error);
      alert('필드 삭제에 실패했습니다.');
    }
  };

  const saveTemplate = () => {
    if (!selectedTemplate || !canvas) return;

    try {
      const objects = canvas.getObjects().filter((obj: any) => obj.data);
      const updatedFields = objects.map((obj: any) => {
        const field = obj.data as TemplateField;
        return {
          ...field,
          x: obj.left || 0,
          y: obj.top || 0,
          width: obj.width || 200,
          height: obj.height || 40
        };
      });

      const updatedTemplate = {
        ...selectedTemplate,
        fields: updatedFields
      };

      setTemplates(prev => prev.map(t => t.id === selectedTemplate.id ? updatedTemplate : t));
      alert('템플릿이 저장되었습니다.');
    } catch (error) {
      console.error('템플릿 저장 오류:', error);
      alert('템플릿 저장에 실패했습니다.');
    }
  };

  const generatePreview = () => {
    if (!canvas) return;

    try {
      const sampleData = {
        '이름': '홍길동',
        '과정명': 'React 개발 과정',
        '발급일': '2024-01-20',
        '기관명': '한국교육원',
        '수료번호': 'CERT-2024-001',
        '교육기간': '2024.01.01 ~ 2024.01.20',
        '교육시간': '40시간',
        '성적': 'A+',
        '담당강사': '김강사',
        '발급기관': '한국교육원'
      };

      const objects = canvas.getObjects().filter((obj: any) => obj.data);
      objects.forEach((obj: any) => {
        const field = obj.data as TemplateField;
        if (field.type !== 'image') {
          obj.set('text', sampleData[field.name as keyof typeof sampleData] || `[${field.name}]`);
        }
      });

      canvas.renderAll();

      setTimeout(() => {
        objects.forEach((obj: any) => {
          const field = obj.data as TemplateField;
          if (field.type !== 'image') {
            obj.set('text', `[${field.name}]`);
          }
        });
        canvas.renderAll();
      }, 3000);
    } catch (error) {
      console.error('미리보기 생성 오류:', error);
      alert('미리보기 생성에 실패했습니다.');
    }
  };

  const exportToPDF = async () => {
    if (!canvas) return;

    try {
      const html2canvas = await import('html2canvas');
      const jsPDF = await import('jspdf');
      
      const canvasElement = canvasRef.current;
      if (!canvasElement) return;

      const canvasImage = await html2canvas.default(canvasElement);
      const imgData = canvasImage.toDataURL('image/png');
      
      const pdf = new jsPDF.default('landscape', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 10, 10, 277, 190);
      pdf.save(`${selectedTemplate?.name || 'template'}.pdf`);
    } catch (error) {
      console.error('PDF 내보내기 오류:', error);
      alert('PDF 내보내기에 실패했습니다.');
    }
  };

  const createTemplate = () => {
    if (!newTemplate.name.trim()) return;

    const template: Template = {
      id: Date.now().toString(),
      name: newTemplate.name,
      description: newTemplate.description,
      backgroundImage: 'https://readdy.ai/api/search-image?query=blank%20certificate%20template%20with%20elegant%20border%20design%2C%20clean%20white%20background%20with%20decorative%20frame%2C%20professional%20layout%20for%20customization&width=800&height=600&seq=blank1&orientation=landscape',
      fields: [],
      createdAt: new Date().toISOString().split('T')[0],
      status: 'draft',
      orientation: 'landscape',
      canvasWidth: 800,
      canvasHeight: 600
    };

    setTemplates(prev => [...prev, template]);
    setNewTemplate({ name: '', description: '' });
    setActiveTab('list');
    alert('새 템플릿이 생성되었습니다.');
  };

  const duplicateTemplate = (template: Template) => {
    const duplicatedTemplate: Template = {
      ...template,
      id: Date.now().toString(),
      name: `${template.name} (복사본)`,
      createdAt: new Date().toISOString().split('T')[0],
      status: 'draft'
    };
    
    setTemplates(prev => [...prev, duplicatedTemplate]);
    setDropdownOpen(null);
  };

  const deleteTemplate = (templateId: string) => {
    if (confirm('정말로 이 템플릿을 삭제하시겠습니까?')) {
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      setDropdownOpen(null);
    }
  };

  const toggleTemplateStatus = (templateId: string) => {
    setTemplates(prev => prev.map(t => 
      t.id === templateId 
        ? { ...t, status: t.status === 'active' ? 'draft' : 'active' }
        : t
    ));
    setDropdownOpen(null);
  };

  const exportTemplate = (template: Template) => {
    const templateData = JSON.stringify(template, null, 2);
    const blob = new Blob([templateData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDropdownOpen(null);
  };

  const addGlobalExcelMapping = () => {
    const nextColumn = String.fromCharCode(65 + globalExcelMappings.length);
    if (nextColumn <= 'Z') {
      const newMappings = [...globalExcelMappings, { column: nextColumn, description: '' }];
      updateGlobalExcelMappings(newMappings);
    }
  };

  const updateGlobalExcelMapping = (index: number, description: string) => {
    const newMappings = globalExcelMappings.map((mapping, i) => 
      i === index ? { ...mapping, description } : mapping
    );
    updateGlobalExcelMappings(newMappings);
  };

  const removeGlobalExcelMapping = (index: number) => {
    const newMappings = globalExcelMappings.filter((_, i) => i !== index);
    updateGlobalExcelMappings(newMappings);
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {!isEditorOpen ? (
          <>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">템플릿 관리</h1>
                <p className="text-gray-600 mt-1">증명서 템플릿을 생성하고 관리합니다</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('list')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                      activeTab === 'list'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    템플릿 목록
                  </button>
                  <button
                    onClick={() => setActiveTab('create')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                      activeTab === 'create'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    새 템플릿 생성
                  </button>
                  <button
                    onClick={() => setActiveTab('mappings')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                      activeTab === 'mappings'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    엑셀 컬럼 매핑
                  </button>
                </nav>
              </div>
            </div>

            {activeTab === 'list' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <div key={template.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
                    <div className="aspect-video bg-gray-50 overflow-hidden relative">
                      <img 
                        src={template.backgroundImage} 
                        alt={template.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          template.status === 'active' 
                            ? 'bg-green-100 text-green-700 border border-green-200' 
                            : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                        }`}>
                          {template.status === 'active' ? '활성' : '초안'}
                        </span>
                      </div>
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 bg-black/70 text-white text-xs rounded-md flex items-center w-fit h-fit justify-center">
                          <i className={`${template.orientation === 'landscape' ? 'ri-landscape-line' : 'ri-smartphone-line'} mr-1`}></i>
                          {template.orientation === 'landscape' ? 'A4 가로' : 'A4 세로'}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="mb-4">
                        <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-1">{template.name}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">{template.description}</p>
                      </div>

                      <div className="flex items-center justify-between mb-6 text-sm text-gray-500">
                        <div className="flex items-center">
                          <i className="ri-calendar-line mr-1"></i>
                          <span>{template.createdAt}</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-file-text-line mr-1"></i>
                          <span>{template.fields.length}개 필드</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button 
                          onClick={() => openEditor(template)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 font-medium whitespace-nowrap"
                        >
                          <i className="ri-edit-line mr-2"></i>
                          편집하기
                        </Button>
                        <div className="relative">
                          <Button 
                            onClick={() => setDropdownOpen(dropdownOpen === template.id ? null : template.id)}
                            className="px-4 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 whitespace-nowrap"
                          >
                            <i className="ri-more-line text-lg"></i>
                          </Button>
                          
                          {dropdownOpen === template.id && (
                            <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-10 py-2">
                              <button
                                onClick={() => duplicateTemplate(template)}
                                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer transition-colors"
                              >
                                <i className="ri-file-copy-line mr-3 text-gray-400"></i>
                                템플릿 복제
                              </button>
                              <button
                                onClick={() => toggleTemplateStatus(template.id)}
                                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer transition-colors"
                              >
                                <i className={`${template.status === 'active' ? 'ri-pause-line' : 'ri-play-line'} mr-3 text-gray-400`}></i>
                                {template.status === 'active' ? '비활성화' : '활성화'}
                              </button>
                              <button
                                onClick={() => exportTemplate(template)}
                                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer transition-colors"
                              >
                                <i className="ri-download-line mr-3 text-gray-400"></i>
                                템플릿 내보내기
                              </button>
                              <div className="border-t border-gray-100 my-1"></div>
                              <button
                                onClick={() => deleteTemplate(template.id)}
                                className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center cursor-pointer transition-colors"
                              >
                                <i className="ri-delete-bin-line mr-3"></i>
                                템플릿 삭제
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div 
                  onClick={() => setActiveTab('create')}
                  className="bg-white rounded-xl shadow-sm border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50/30 transition-all duration-200 cursor-pointer group"
                >
                  <div className="aspect-video flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                        <i className="ri-add-line text-2xl text-blue-600"></i>
                      </div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">새 템플릿 생성</h3>
                      <p className="text-gray-600 text-sm">새로운 증명서 템플릿을 만들어보세요</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-center">
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 font-medium whitespace-nowrap">
                        <i className="ri-add-line mr-2"></i>
                        템플릿 생성하기
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === 'create' ? (
              <div className="max-w-2xl">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">새 템플릿 생성</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">템플릿 이름</label>
                      <Input
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="템플릿 이름을 입력하세요"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">설명</label>
                      <textarea
                        value={newTemplate.description}
                        onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="템플릿 설명을 입력하세요"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        maxLength={500}
                      />
                    </div>
                    <Button 
                      onClick={createTemplate}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={!newTemplate.name.trim()}
                    >
                      <i className="ri-add-line mr-2"></i>
                      템플릿 생성
                    </Button>
                  </div>
                </Card>
              </div>
            ) : (
              <div className="max-w-4xl">
                <Card className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-lg font-semibold">전역 엑셀 컬럼 매핑</h3>
                      <p className="text-sm text-gray-600 mt-1">모든 템플릿에서 공통으로 사용할 엑셀 컬럼 매핑을 설정합니다</p>
                    </div>
                    <Button
                      onClick={addGlobalExcelMapping}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
                      disabled={globalExcelMappings.length >= 26}
                    >
                      <i className="ri-add-line mr-2"></i>
                      컬럼 추가
                    </Button>
                  </div>
                  
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {globalExcelMappings.map((mapping, index) => (
                      <div key={mapping.column} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-lg font-bold text-blue-600">
                          {mapping.column}
                        </div>
                        <Input
                          value={mapping.description}
                          onChange={(e) => updateGlobalExcelMapping(index, e.target.value)}
                          placeholder="필드 설명을 입력하세요"
                          className="flex-1"
                        />
                        <Button
                          onClick={() => removeGlobalExcelMapping(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 border border-red-300 whitespace-nowrap"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">사용 안내</h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>• 이 매핑은 모든 템플릿에서 공통으로 사용됩니다</p>
                      <p>• 템플릿 편집 시 이 매핑 정보를 참고하여 필드를 배치하세요</p>
                      <p>• 엑셀 파일 업로드 시 이 컬럼 순서에 맞춰 데이터를 입력하세요</p>
                      <p>• 변경사항은 자동으로 저장됩니다</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b p-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">{selectedTemplate?.name} 편집</h2>
              <div className="flex gap-2">
                <Button onClick={generatePreview} className="border border-gray-300 hover:bg-gray-50 whitespace-nowrap">
                  <i className="ri-eye-line mr-2"></i>
                  미리보기
                </Button>
                <Button onClick={exportToPDF} className="border border-gray-300 hover:bg-gray-50 whitespace-nowrap">
                  <i className="ri-download-line mr-2"></i>
                  PDF 내보내기
                </Button>
                <Button onClick={saveTemplate} className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap">
                  <i className="ri-save-line mr-2"></i>
                  저장
                </Button>
                <Button onClick={closeEditor} className="border border-gray-300 hover:bg-gray-50 whitespace-nowrap">
                  <i className="ri-close-line mr-2"></i>
                  닫기
                </Button>
              </div>
            </div>

            {/* 탭 패널 - 상단 배치 */}
            <div className="border-b bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex">
                  <button
                    onClick={() => { setLeftPanelTab('columns'); setIsPanelCollapsed(false); }}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                      leftPanelTab === 'columns' && !isPanelCollapsed
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-table-line mr-1"></i>
                    엑셀/이미지
                  </button>
                  <button
                    onClick={() => { setLeftPanelTab('canvas-info'); setIsPanelCollapsed(false); }}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                      leftPanelTab === 'canvas-info' && !isPanelCollapsed
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-information-line mr-1"></i>
                    캔버스
                  </button>
                  <button
                    onClick={() => { setLeftPanelTab('properties'); setIsPanelCollapsed(false); }}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                      leftPanelTab === 'properties' && !isPanelCollapsed
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-settings-3-line mr-1"></i>
                    속성
                  </button>
                </div>
                <button
                  onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className={`ri-arrow-${isPanelCollapsed ? 'down' : 'up'}-s-line text-lg`}></i>
                </button>
              </div>
            </div>

            {/* 탭 컨텐츠 - 접을 수 있는 컴팩트 레이아웃 */}
            {!isPanelCollapsed && (
              <div className="border-b bg-white p-4">
                {/* 엑셀 컬럼 & 이미지 탭 */}
                {leftPanelTab === 'columns' && (
                  <div className="flex gap-6">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm text-gray-900">엑셀 컬럼</h3>
                        <span className="text-xs text-gray-500">드래그하여 추가</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {globalExcelMappings.map((mapping) => (
                          <div
                            key={mapping.column}
                            draggable
                            onDragStart={(e) => handleColumnDragStart(e, mapping)}
                            className="bg-blue-50 border border-blue-200 rounded px-2 py-1 cursor-move hover:border-blue-400 hover:bg-blue-100 transition-all flex items-center gap-1.5"
                          >
                            <span className="w-5 h-5 bg-blue-600 rounded text-xs font-bold text-white flex items-center justify-center flex-shrink-0">
                              {mapping.column}
                            </span>
                            <span className="text-xs text-gray-700 truncate max-w-20">{mapping.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="w-px bg-gray-200"></div>

                    <div className="w-48 flex-shrink-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm text-gray-900">PNG 이미지</h3>
                        <input
                          ref={imageUploadRef}
                          type="file"
                          accept="image/png"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <button
                          onClick={() => imageUploadRef.current?.click()}
                          className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
                        >
                          <i className="ri-upload-line mr-1"></i>
                          업로드
                        </button>
                      </div>
                      
                      {uploadedImages.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {uploadedImages.map((image) => (
                            <div
                              key={image.id}
                              draggable
                              onDragStart={(e) => handleImageDragStart(e, image)}
                              className="w-10 h-10 border border-purple-200 rounded cursor-move hover:border-purple-400 transition-all overflow-hidden"
                              title={image.name}
                            >
                              <img src={image.url} alt={image.name} className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">이미지 없음</p>
                      )}
                    </div>
                  </div>
                )}

                {/* 캔버스 정보 탭 */}
                {leftPanelTab === 'canvas-info' && (
                  <div className="flex gap-6">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm mb-2 text-gray-900">캔버스 방향</h4>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => changeCanvasOrientation('landscape')}
                          className={`whitespace-nowrap cursor-pointer text-xs px-3 py-1.5 ${
                            canvasOrientation === 'landscape'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <i className="ri-landscape-line mr-1"></i>
                          가로
                        </Button>
                        <Button
                          onClick={() => changeCanvasOrientation('portrait')}
                          className={`whitespace-nowrap cursor-pointer text-xs px-3 py-1.5 ${
                            canvasOrientation === 'portrait'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <i className="ri-layout-line mr-1"></i>
                          세로
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {canvasOrientation === 'landscape' ? '1123×794px' : '794×1123px'}
                      </p>
                    </div>

                    <div className="w-px bg-gray-200"></div>

                    <div className="flex-1">
                      <h4 className="font-medium text-sm mb-2 text-gray-900">배경 설정</h4>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={backgroundSettings.color}
                          onChange={(e) => updateBackgroundColor(e.target.value)}
                          className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          className="text-xs px-2 py-1.5 border border-gray-300 hover:bg-gray-50 whitespace-nowrap"
                        >
                          <i className="ri-image-line mr-1"></i>
                          이미지
                        </Button>
                        <Button
                          onClick={removeBackground}
                          className="text-xs px-2 py-1.5 text-red-600 border border-red-300 hover:bg-red-50 whitespace-nowrap"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                      </div>
                    </div>

                    <div className="w-px bg-gray-200"></div>

                    <div className="flex-1">
                      <h4 className="font-medium text-sm mb-2 text-gray-900">엑셀 매핑</h4>
                      <button
                        onClick={() => {
                          closeEditor();
                          setActiveTab('mappings');
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        <i className="ri-external-link-line mr-1"></i>
                        전역 매핑 설정
                      </button>
                    </div>
                  </div>
                )}

                {/* 속성 설정 탭 */}
                {leftPanelTab === 'properties' && (
                  <div>
                    {selectedField ? (
                      <div className="flex gap-4 items-start">
                        {selectedField.type !== 'image' && (
                          <>
                            <div className="flex-shrink-0 w-32">
                              <label className="block text-xs font-medium mb-1 text-gray-700">필드명</label>
                              <Input
                                value={selectedField.name}
                                onChange={(e) => updateField({ name: e.target.value })}
                                placeholder="필드 이름"
                                className="py-1.5 text-sm"
                              />
                            </div>

                            <div className="flex-shrink-0 w-24">
                              <label className="block text-xs font-medium mb-1 text-gray-700">컬럼</label>
                              <select 
                                value={selectedField.excelColumn}
                                onChange={(e) => updateField({ excelColumn: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                {globalExcelMappings.map(mapping => (
                                  <option key={mapping.column} value={mapping.column}>
                                    {mapping.column}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="flex-shrink-0 w-16">
                              <label className="block text-xs font-medium mb-1 text-gray-700">크기</label>
                              <Input
                                type="number"
                                value={selectedField.fontSize}
                                onChange={(e) => updateField({ fontSize: parseInt(e.target.value) })}
                                min="8"
                                max="72"
                                className="py-1.5 text-sm"
                              />
                            </div>

                            <div className="flex-shrink-0 w-24">
                              <label className="block text-xs font-medium mb-1 text-gray-700">굵기</label>
                              <select 
                                value={selectedField.fontWeight}
                                onChange={(e) => updateField({ fontWeight: e.target.value as any })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="normal">보통</option>
                                <option value="bold">굵게</option>
                                <option value="300">가늘게</option>
                              </select>
                            </div>

                            <div className="flex-shrink-0">
                              <label className="block text-xs font-medium mb-1 text-gray-700">색상</label>
                              <input
                                type="color"
                                value={selectedField.color}
                                onChange={(e) => updateField({ color: e.target.value })}
                                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                              />
                            </div>

                            <div className="flex-shrink-0">
                              <label className="block text-xs font-medium mb-1 text-gray-700">정렬</label>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => updateField({ align: 'left' })}
                                  className={`w-7 h-8 flex items-center justify-center rounded cursor-pointer ${selectedField.align === 'left' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                                >
                                  <i className="ri-align-left text-sm"></i>
                                </button>
                                <button
                                  onClick={() => updateField({ align: 'center' })}
                                  className={`w-7 h-8 flex items-center justify-center rounded cursor-pointer ${selectedField.align === 'center' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                                >
                                  <i className="ri-align-center text-sm"></i>
                                </button>
                                <button
                                  onClick={() => updateField({ align: 'right' })}
                                  className={`w-7 h-8 flex items-center justify-center rounded cursor-pointer ${selectedField.align === 'right' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                                >
                                  <i className="ri-align-right text-sm"></i>
                                </button>
                              </div>
                            </div>
                          </>
                        )}

                        <div className="ml-auto flex-shrink-0">
                          <label className="block text-xs font-medium mb-1 text-gray-700">&nbsp;</label>
                          <Button
                            onClick={deleteSelectedField}
                            className="px-3 py-1.5 text-red-600 border border-red-300 hover:bg-red-50 text-sm whitespace-nowrap"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>
                            삭제
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-2">
                        <i className="ri-cursor-line mr-2"></i>
                        캔버스에서 요소를 선택하세요
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 캔버스 영역 */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div 
                className="flex-1 overflow-auto bg-gray-50 p-6 flex items-center justify-center"
                onDrop={handleCanvasDrop}
                onDragOver={handleCanvasDragOver}
              >
                {!isCanvasReady && (
                  <div className="flex items-center justify-center h-full bg-white rounded-lg shadow-lg">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-gray-600">템플릿 편집기 로딩 중...</p>
                    </div>
                  </div>
                )}
                <canvas 
                  ref={canvasRef} 
                  className={`border-2 border-gray-300 rounded-lg shadow-2xl bg-white ${!isCanvasReady ? 'hidden' : ''}`}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%',
                    height: 'auto'
                  }}
                ></canvas>
              </div>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={uploadBackgroundImage}
              className="hidden"
            />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
